import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Inbox, Loader2, Plus, Check, X } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { ExclusivityBadge } from '../ideation/briefs/BriefCreatorList';
import { api, budgetText, dateText, errMsg, STATUS_STYLE } from '../ideation/briefs/briefApi';

const fmt = (n) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n);
const TH = 'px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400';
const TD = 'px-3 py-2 text-xs text-[#0F0F0F] dark:text-white';

const BriefCard = ({ b, active, onClick }) => (
  <button onClick={onClick} className={`w-full text-left rounded-xl border p-3 transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/[0.05]' : 'border-black/[0.08] dark:border-white/5 hover:border-[#FF451A]/40'}`} data-testid={`tm-brief-card-${b.id}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="font-semibold text-sm text-[#0F0F0F] dark:text-white truncate">{b.brand_name}</div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[b.status]}`}>{b.status}</span>
    </div>
    <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 mt-1">{b.deliverable} · {(b.niche || []).join(', ') || 'Any niche'}</div>
    <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400">{budgetText(b)} · Due {dateText(b.deadline)}</div>
  </button>
);

const MatchRow = ({ c, onAdd, onRemove, busy, status }) => (
  <tr className="border-t border-black/[0.06] dark:border-white/5" data-testid={`tm-match-row-${c.creator_id}`}>
    <td className={TD}><div className="font-semibold">{c.name}</div>{c.instagram_handle && <span className="text-[10px] text-[#FF451A]">@{c.instagram_handle}</span>}</td>
    <td className={TD}><ExclusivityBadge label={c.priority_label} /></td>
    <td className={`${TD} font-mono`}>{fmt(c.instagram_followers)}</td>
    <td className={TD}>{c.quality_tier || '—'}</td>
    <td className={`${TD} max-w-[160px] truncate`}>{c.category || '—'}</td>
    <td className={TD}>{c.city || '—'}</td>
    <td className={TD}>
      {c.added ? (
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold" data-testid={`tm-added-${c.creator_id}`}><Check size={12} /> {c.added_by_me ? 'Added' : `Added by ${c.added_by_name || 'another TM'}`}</span>
          {c.added_by_me && (
            <button onClick={() => onRemove(c)} disabled={busy || status === 'Closed'} title="Remove from brief" className="rounded-full px-2 py-0.5 text-[11px] font-medium inline-flex items-center gap-1 border border-black/[0.12] dark:border-white/[0.08] text-[#6B6B6B] hover:text-red-500 hover:border-red-500/40 disabled:opacity-40 transition-colors" data-testid={`tm-remove-${c.creator_id}`}>
              {busy ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />} Remove
            </button>
          )}
        </div>
      ) : (
        <button onClick={() => onAdd(c)} disabled={busy || status !== 'Active'} className="rounded-full px-3 py-1 text-[11px] font-semibold inline-flex items-center gap-1 bg-[#FF451A] hover:bg-[#E63E17] text-white disabled:opacity-40 transition-colors" data-testid={`tm-add-${c.creator_id}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add to Brief
        </button>
      )}
      {c.already_shortlisted && <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-400 mt-0.5">AE already shortlisted</div>}
    </td>
  </tr>
);

export default function TmBriefs() {
  const [params, setParams] = useSearchParams();
  const [briefs, setBriefs] = useState(null);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const activeId = params.get('brief');

  useEffect(() => { api.get('').then((d) => setBriefs(d.briefs)).catch(() => setBriefs([])); }, []);
  const loadMatches = useCallback(() => {
    if (!activeId) { setData(null); return; }
    api.get(`/${activeId}/my-matches${showAll ? '?show_all=true' : ''}`).then(setData).catch((e) => setData({ error: errMsg(e, 'Could not load brief') }));
  }, [activeId, showAll]);
  useEffect(loadMatches, [loadMatches]);

  const add = async (c) => {
    setBusy(c.creator_id);
    try {
      await api.post(`/${activeId}/additions`, { creator_id: c.creator_id });
      toast.success(`${c.name} added to ${data.brief.brand_name}`);
      setData((d) => ({ ...d, creators: d.creators.map((x) => (x.creator_id === c.creator_id ? { ...x, added: true, added_by_me: true } : x)), my_added_count: d.my_added_count + 1 }));
    } catch (e) {
      toast.error(errMsg(e, 'Could not add creator'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from ${data.brief.brand_name}?`)) return;
    setBusy(c.creator_id);
    try {
      await api.del(`/${activeId}/additions/${c.creator_id}`);
      toast.success(`${c.name} removed from ${data.brief.brand_name}`);
      setData((d) => ({ ...d, creators: d.creators.map((x) => (x.creator_id === c.creator_id ? { ...x, added: false, added_by_me: false, added_by_name: null } : x)), my_added_count: d.my_added_count - 1 }));
    } catch (e) {
      toast.error(errMsg(e, 'Could not remove creator'));
    } finally {
      setBusy(null);
    }
  };

  const b = data?.brief;
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="tm-briefs-page">
        <div className="max-w-[1400px] mx-auto space-y-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3"><Inbox className="text-[#FF451A]" size={30} /> Briefs</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Briefs shared with you by Account Executives. Add your exclusive creators who fit.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            <div className="space-y-2" data-testid="tm-briefs-list">
              {briefs === null ? <Loader2 size={16} className="animate-spin" /> : briefs.length === 0 ? <p className="text-sm text-[#6B6B6B] dark:text-zinc-400" data-testid="tm-briefs-empty">No briefs shared with you yet.</p> :
                briefs.map((x) => <BriefCard key={x.id} b={x} active={x.id === activeId} onClick={() => setParams({ brief: x.id })} />)}
            </div>
            <div className="min-w-0">
              {!activeId ? <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 border border-dashed border-black/[0.12] dark:border-white/10 rounded-2xl p-10 text-center">Pick a brief to see your matching creators.</div> : !data ? <Loader2 size={16} className="animate-spin" /> : data.error ? <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 border border-dashed border-black/[0.12] dark:border-white/10 rounded-2xl p-10 text-center" data-testid="tm-brief-error">{data.error}</div> : (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-black/[0.08] dark:border-white/5 p-5" data-testid="tm-brief-detail">
                    <div className="text-xl font-bold text-[#0F0F0F] dark:text-white">{b.brand_name} · {b.deliverable}</div>
                    {b.objective && <p className="text-sm text-[#4B5563] dark:text-zinc-300 mt-1">{b.objective}</p>}
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-2">Niche: {(b.niche || []).join(', ') || 'Any'} · Budget: {budgetText(b)} · Due {dateText(b.deadline)} · Tier: {(b.quality_tier || []).join(', ') || 'Any'} · Location: {[...(b.city || []), ...(b.state || [])].join(', ') || 'Any'}</div>
                    {b.notes && <p className="text-xs text-[#4B5563] dark:text-zinc-300 mt-2 whitespace-pre-wrap">{b.notes}</p>}
                  </div>
                  {b.status !== 'Active' && <div className="rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs px-3 py-2" data-testid="tm-brief-paused-note">This brief is {b.status} — the AE isn't accepting new creators right now.</div>}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-[#0F0F0F] dark:text-white" data-testid="tm-matches-summary">{showAll ? <><span className="font-bold">{data.creators.length}</span> of your creators</> : <><span className="font-bold">{data.match_count}</span> of your creators match</>} · <span className="font-bold text-[#FF451A]">{data.my_added_count}</span> added by you</div>
                    <label className="text-xs inline-flex items-center gap-2 text-[#0F0F0F] dark:text-white cursor-pointer">
                      <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-[#FF451A]" data-testid="tm-show-all-toggle" /> Show all my creators
                    </label>
                  </div>
                  {data.creators.length === 0 ? <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 border border-dashed border-black/[0.12] dark:border-white/10 rounded-2xl p-8 text-center" data-testid="tm-matches-empty">None of your creators match this brief's filters. Try "Show all my creators".</div> : (
                    <div className="overflow-x-auto border border-black/[0.08] dark:border-white/5 rounded-2xl" data-testid="tm-matches-table">
                      <table className="w-full">
                        <thead><tr>{['Creator', 'Exclusivity', 'Followers', 'Tier', 'Category', 'City', ''].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                        <tbody>{data.creators.map((c) => <MatchRow key={c.creator_id} c={c} onAdd={add} onRemove={remove} busy={busy === c.creator_id} status={b.status} />)}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
